// Tax Sources Data Management Utility
import taxSourcesData from '../data/tax_sources.json';

/**
 * Get all tax sources from the JSON data file
 * @returns {Array} Array of tax source objects
 */
export function getTaxSources() {
  return taxSourcesData.taxSources.filter(source => source.enabled);
}

/**
 * Get a specific tax source by ID
 * @param {string} id - The tax source ID
 * @returns {Object|null} The tax source object or null if not found
 */
export function getTaxSourceById(id) {
  return taxSourcesData.taxSources.find(source => source.id === id) || null;
}

/**
 * Get the total basic rate reduction for active simulations
 * @param {Object} activeSimulations - Object with simulation IDs as keys and boolean values
 * @returns {number} Total basic rate reduction (e.g., 0.04 for 4%)
 */
export function getBasicRateReduction(activeSimulations) {
  let totalReduction = 0;
  
  for (const [id, isActive] of Object.entries(activeSimulations)) {
    if (isActive) {
      const source = getTaxSourceById(id);
      if (source) {
        totalReduction += source.basicRateReduction;
      }
    }
  }
  
  return totalReduction;
}

/**
 * Get the total annual revenue for active simulations
 * @param {Object} activeSimulations - Object with simulation IDs as keys and boolean values
 * @returns {number} Total annual revenue in pounds
 */
export function getAdditionalRevenue(activeSimulations) {
  let totalRevenue = 0;
  
  for (const [id, isActive] of Object.entries(activeSimulations)) {
    if (isActive) {
      const source = getTaxSourceById(id);
      if (source) {
        totalRevenue += source.annualRevenue / 1000000000; // Convert to billions
      }
    }
  }
  
  return totalRevenue;
}

/**
 * Initialize simulation state object from tax sources
 * @returns {Object} Initial simulation state with all sources set to false
 */
export function getInitialSimulationState() {
  const state = {};
  getTaxSources().forEach(source => {
    state[source.id] = false;
  });
  return state;
}

/**
 * Get toggle data formatted for the SimulationToggles component
 * @returns {Array} Array of toggle objects for rendering
 */
export function getToggleData() {
  return getTaxSources().map(source => ({
    id: source.id,
    title: source.title,
    description: source.description,
    icon: source.icon,
    revenue: source.revenueDisplay,
    source: source.sourceName,
    sourceUrl: source.sourceUrl,
  }));
}

/**
 * Get the data version
 * @returns {string} Version string from the JSON file
 */
export function getDataVersion() {
  return taxSourcesData.version;
}

/**
 * Get the last updated date
 * @returns {string} Last updated date string from the JSON file
 */
export function getLastUpdated() {
  return taxSourcesData.lastUpdated;
}
